import { ChangeDetectionStrategy, Component, inject, Input, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap, take, tap } from 'rxjs';
import { AddCustomPlayerComponent } from '../add-custom-player/add-custom-player.component';
import {
  ActionModalComponent,
  AppRoutes,
  ButtonLabels,
  GameDetailsModel,
  GamePlayerModel,
  GamesRoutes,
  HostedGamesRoutes,
  LOADING_TOKEN,
  LoadingProvider,
  MODAL_TOKEN,
  ModalProvider,
  TOAST_TOKEN,
  ToastProvider,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { Router } from '@angular/router';
import {
  GenerateReassignUrlCommandHandler,
  RemoveGameCommandHandler,
  UpdateGameCommandHandler
} from '../../application/handlers';
import { HostedGamesService } from '@hosted-games/infra';
import { CreateGameViewService } from '../../application/view-services';
import { SendApplicationDirective } from '../../../../../games/src/lib/application/directives';
import { GameApplicationsService } from '@games/infra';
import { SendApplicationCommandHandler } from '../../../../../games/src/lib/application/handlers';
import { UserRoleEnum, Visibility } from '@core-http';
import { OrIconComponent } from '@ui-components';

@Component({
  selector: 'lib-manage-hosted-game',
  templateUrl: './manage-hosted-game.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SendApplicationDirective, OrIconComponent],
  providers: [
    RemoveGameCommandHandler,
    HostedGamesService,
    UpdateGameCommandHandler,
    SendApplicationCommandHandler,
    GameApplicationsService,
    GenerateReassignUrlCommandHandler
  ]
})
export class ManageHostedGameComponent {
  @Input() game!: GameDetailsModel;
  @Input() hostId!: string;

  private readonly removeGameCommandHandler: RemoveGameCommandHandler = inject(RemoveGameCommandHandler);
  private readonly updateGameCommandHandler: UpdateGameCommandHandler = inject(UpdateGameCommandHandler);
  private readonly router: Router = inject(Router);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);
  private readonly loadingProvider: LoadingProvider = inject(LOADING_TOKEN);
  private readonly createGameViewService: CreateGameViewService = inject(CreateGameViewService);
  private readonly generateReassignUrlCommandHandler: GenerateReassignUrlCommandHandler = inject(
    GenerateReassignUrlCommandHandler
  );
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);

  readonly isAdmin: Signal<boolean | undefined> = toSignal(
    this.userDataProvider.userData$.pipe(map((data: UserModel) => data.role === UserRoleEnum.ADMIN))
  );

  get visibilityLabel(): ButtonLabels {
    const isPrivate: boolean = this.game?.visibility === Visibility.PRIVATE;
    const header: string = isPrivate ? 'Zmień gierkę na publiczną' : 'Zmień gierkę na prywatną';

    const text: string = isPrivate
      ? 'Twoja gierka zostanie dodana na mapę gier'
      : 'Twoja gierka zostanie ukryta na mapie gier';
    return { header, text };
  }

  get isPlayerInGame(): boolean | undefined {
    return this.game?.players?.some((player: GamePlayerModel) => player.userId === this.hostId);
  }

  addPlayer(): void {
    this.modalProvider
      .showModal$({
        component: AddCustomPlayerComponent,
        cssClass: 'present-modal',
        componentProps: {
          gameId: this.game.gameId
        }
      })
      .subscribe(() => this.modalProvider.dismissAllStoredModals());
  }

  removeGame(): void {
    const shortMessage: string = 'Czy na pewno chcesz usunąć organizowaną gierkę?';
    const longMessage: string =
      'Czy na pewno chcesz usunąć organizowaną gierkę? Gracze zapisani do gry dostaną powiadomienie o usunięciu gry';

    this.modalProvider.dismissAllStoredModals();

    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Usuń organizowaną gierkę',
        message: this.game.players?.length ? longMessage : shortMessage,
        btnOk: 'Tak, usuń gierkę',
        btnCancel: 'Anuluj',
        action: () =>
          this.removeGameCommandHandler
            .removeGame(this.game.gameId)
            .pipe(
              take(1),
              switchMap(() => this.router.navigate([AppRoutes.MY_GAMES]))
            )
            .subscribe(() => {
              this.toastProvider.showToast({ message: 'Usunięto gierkę' });
              this.modalProvider.dismissAllStoredModals();
            })
      }
    });
  }

  editGame(): void {
    this.createGameViewService.setGame(this.game);
    this.modalProvider.dismissAllStoredModals();
    this.router.navigateByUrl(`${AppRoutes.HOST}/${HostedGamesRoutes.CREATE_GAME}?mode=edit`);
  }

  changeVisibility(): void {
    this.updateGameCommandHandler
      .updateGame(this.game.gameId, {
        visibility: this.game.visibility === Visibility.PUBLIC ? Visibility.PRIVATE : Visibility.PUBLIC
      })
      .pipe(
        take(1),
        tap(() => this.modalProvider.dismissAllStoredModals())
      )
      .subscribe(() =>
        this.toastProvider.showToast({
          message: 'Zmieniono widoczność gierki'
        })
      );
  }

  goToInvitations(): void {
    this.modalProvider.dismissAllStoredModals();
    this.router.navigate([
      `${AppRoutes.HOST}/${HostedGamesRoutes.FUTURE}/${this.game.gameId}/${HostedGamesRoutes.INVITE}`
    ]);
  }

  goToLineups(): void {
    this.modalProvider.dismissAllStoredModals();
    this.router.navigate([`${AppRoutes.GAMES}/${GamesRoutes.LINEUPS}/${this.game.gameId}`]);
  }

  generateReassignUrl(): void {
    this.loadingProvider
      .showLoading$()
      .pipe(
        switchMap(() => this.generateReassignUrlCommandHandler.generate(this.game.gameId)),
        tap(() => this.loadingProvider.dismissLoading()),
        tap(() => this.modalProvider.dismissAllStoredModals())
      )
      .subscribe();
  }
}
